import { createElement, StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Suspense } from 'react'
import './lib/i18n.ts'   // ← initialise i18next before anything renders
import './index.css'
import './ui-consistency.css'
import App from './App.tsx'

const rootElement = document.getElementById('root')
const pathname = window.location.pathname
const adminBasename = pathname === '/account/admin' || pathname.startsWith('/account/admin/')
  ? '/account/admin'
  : pathname === '/admin' || pathname.startsWith('/admin/')
    ? '/admin'
    : null

if (adminBasename) {
  Promise.all([
    import('./admin/adminStyles.ts'),
    import('./admin/AdminApp.tsx'),
  ]).then(([, { AdminApp }]) => {
    document.body.classList.toggle('admin-account-mode', adminBasename === '/account/admin')
    createRoot(rootElement).render(
      <StrictMode>
        <BrowserRouter basename={adminBasename}>
          {createElement(AdminApp)}
        </BrowserRouter>
      </StrictMode>
    )
  })
} else {
  const app = (
    <HelmetProvider>
      <BrowserRouter>
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  )

  // The server pre-renders body markup for home and company pages. Hydrate it:
  // matched markup is kept (the static pre-render stays the first paint and
  // usually the LCP element), and React re-renders only the subtrees that
  // legitimately differ (live counts, ordering, locale timing). Mismatch
  // reports are swallowed — client-side recovery is the expected path here and
  // a console error would fail the best-practices audit on every page load.
  hydrateRoot(rootElement, app, {
    onRecoverableError: (error, errorInfo) => {
      // Client-side recovery is the expected path for mismatched subtrees.
      // Keep a bounded breadcrumb trail for debugging instead of letting these
      // surface as console errors (which would fail the best-practices audit).
      try {
        const w = window as any;
        w.__AC_HYDRATION_ISSUES = (w.__AC_HYDRATION_ISSUES || []).slice(-9);
        w.__AC_HYDRATION_ISSUES.push({
          message: String((error as any)?.message || error).slice(0, 300),
          stack: String((errorInfo as any)?.componentStack || '').slice(0, 600),
        });
      } catch {}
    },
  })
}
